// دیوارِ منحنی (و «مرزِ فضا»ی منحنی): همان دیوارِ معمولی با یک عددِ اضافه — arc = «خیز» به سانتی‌متر: فاصله‌ی وسطِ قوس
// از وسطِ خطِ راستِ بینِ دو سر، به سمتِ چپِ جهتِ دیوار (x1→x2). صفر/نبودن = دیوارِ صاف.
// برای هر محاسبه‌ای (فضاها، کف/سقف، سه‌بعدی، راه رفتن) قوس به چند تکه‌ی صافِ کوتاه شکسته می‌شود (expandArcs) —
// تکه‌ها آن‌قدر کوتاه‌اند (حداکثر ~۳۰ سانت) که درزی دیده نمی‌شود؛ خودِ پلان فقط همان یک دیوار را نگه می‌دارد.

export const isArc = (w) => Boolean(w) && Math.abs(Number(w.arc) || 0) > 0.5;

// بیشترین خیز = نیم‌دایره
export const maxArc = (w) => Math.hypot(w.x2 - w.x1, w.y2 - w.y1) / 2;

export function arcGeom(w) {
  const dx = w.x2 - w.x1;
  const dy = w.y2 - w.y1;
  const L = Math.hypot(dx, dy) || 1;
  const ux = dx / L;
  const uy = dy / L;
  const nx = -uy;
  const ny = ux;
  const h = Math.max(-L / 2, Math.min(L / 2, Number(w.arc) || 0));
  const mx = (w.x1 + w.x2) / 2;
  const my = (w.y1 + w.y2) / 2;
  const apex = { x: mx + nx * h, y: my + ny * h };
  if (Math.abs(h) < 0.5) return { straight: true, L, apex, mid: { x: mx, y: my }, nx, ny, h: 0 };
  const R = (L * L) / 4 / (2 * Math.abs(h)) + Math.abs(h) / 2;
  const s = Math.sign(h);
  const cx = mx + nx * (h - s * R);
  const cy = my + ny * (h - s * R);
  const a1 = Math.atan2(w.y1 - cy, w.x1 - cx);
  const aA = Math.atan2(apex.y - cy, apex.x - cx);
  // از سرِ اول تا سرِ دوم، از طرفی که از رأس می‌گذرد
  let d1 = aA - a1;
  while (d1 > Math.PI) d1 -= Math.PI * 2;
  while (d1 < -Math.PI) d1 += Math.PI * 2;
  const sweep = d1 * 2;
  return { straight: false, L, R, cx, cy, a1, sweep, apex, mid: { x: mx, y: my }, nx, ny, h, length: Math.abs(sweep) * R };
}

// نقطه‌های قوس (هر دو سر دقیقاً همان سرهای دیوار)
export function arcPoints(w, segLen = 30) {
  const g = arcGeom(w);
  if (g.straight) return [{ x: w.x1, y: w.y1 }, { x: w.x2, y: w.y2 }];
  const n = Math.max(6, Math.min(48, Math.ceil(g.length / segLen)));
  const pts = [{ x: w.x1, y: w.y1 }];
  for (let k = 1; k < n; k += 1) {
    const a = g.a1 + (g.sweep * k) / n;
    pts.push({ x: g.cx + Math.cos(a) * g.R, y: g.cy + Math.sin(a) * g.R });
  }
  pts.push({ x: w.x2, y: w.y2 });
  return pts;
}

// نقطه‌های قوس به‌صورتِ آرایه‌ی صاف برای Konva ([x1, y1, x2, y2, ...])
export const arcFlat = (w) => arcPoints(w, 12).flatMap((p) => [p.x, p.y]);

// دیوارهای منحنی → تکه‌های صاف (شناسه‌ی «id~k»، arcParent = شناسه‌ی اصلی) — بقیه‌ی دیوارها دست‌نخورده
export function expandArcs(walls) {
  if (!walls || !walls.some(isArc)) return walls;
  const out = [];
  walls.forEach((w) => {
    if (!isArc(w)) {
      out.push(w);
      return;
    }
    const pts = arcPoints(w);
    const { arc, ...rest } = w;
    for (let k = 0; k < pts.length - 1; k += 1) {
      out.push({ ...rest, id: `${w.id}~${k}`, arcParent: w.id, x1: pts[k].x, y1: pts[k].y, x2: pts[k + 1].x, y2: pts[k + 1].y });
    }
  });
  return out;
}

// شناسه‌ی تکه («12~3») → شناسه‌ی دیوارِ اصلی
export const parentWallId = (id) => {
  if (typeof id === 'string' && id.includes('~')) {
    const base = id.split('~')[0];
    const num = Number(base);
    return Number.isFinite(num) && String(num) === base ? num : base;
  }
  return id;
};

// فاصله‌ی نقطه تا قوس (برای لمس/نزدیک‌ترین دیوار)
export function distToWall(w, px, py) {
  const pts = isArc(w) ? arcPoints(w, 15) : [{ x: w.x1, y: w.y1 }, { x: w.x2, y: w.y2 }];
  let best = Infinity;
  for (let k = 0; k < pts.length - 1; k += 1) {
    const a = pts[k];
    const b = pts[k + 1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const l2 = dx * dx + dy * dy || 1;
    const t = Math.max(0, Math.min(1, ((px - a.x) * dx + (py - a.y) * dy) / l2));
    best = Math.min(best, Math.hypot(px - (a.x + dx * t), py - (a.y + dy * t)));
  }
  return best;
}
